import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CursorPaginationProps {
  /** Index of the page being shown, zero-based; the caller keeps the stack of cursors that got here. */
  pageIndex: number;
  rowsPerPage: number;
  /** Rows on the page being shown. */
  rowsOnPage: number;
  totalCount: number;
  /** True when the server stopped counting; the total then means "at least this many". */
  totalCountIsCapped: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onRowsPerPageChange: (value: number) => void;
  rowsPerPageOptions?: number[];
}

const defaultOptions = [50, 100, 500, 1000];
const numberFormat = new Intl.NumberFormat();

/**
 * Paging over a keyset cursor: forward and back only, no jump to a page number. The server hands back a cursor
 * for the row after the page, so a page is reached by walking, and the stack of cursors walked is what "back"
 * pops. A capped total reads as "more than", never as a page count somebody could type in.
 */
const CursorPagination = ({
  pageIndex,
  rowsPerPage,
  rowsOnPage,
  totalCount,
  totalCountIsCapped,
  hasNext,
  onPrevious,
  onNext,
  onRowsPerPageChange,
  rowsPerPageOptions = defaultOptions,
}: CursorPaginationProps) => {
  const from = rowsOnPage === 0 ? 0 : pageIndex * rowsPerPage + 1;
  const to = pageIndex * rowsPerPage + rowsOnPage;
  const total = totalCountIsCapped ? `more than ${numberFormat.format(totalCount)}` : numberFormat.format(totalCount);

  return (
    <div className="bg-muted/50 border-t shrink-0 flex items-center justify-end gap-4 px-4 py-2">
      <div className="flex items-center gap-2">
        <Label className="text-sm whitespace-nowrap">Rows per page:</Label>
        <Select value={String(rowsPerPage)} onValueChange={(val) => onRowsPerPageChange(Number(val))}>
          <SelectTrigger size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {rowsPerPageOptions.map((opt) => (
              <SelectItem key={opt} value={String(opt)}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span className="min-w-[80px] text-center text-sm">
        {numberFormat.format(from)}&ndash;{numberFormat.format(to)} of {total}
      </span>
      <div className="flex gap-1">
        <Button variant="outline" size="icon-sm" disabled={pageIndex === 0} onClick={onPrevious}>
          <ChevronLeft className="size-4" />
        </Button>
        <Button variant="outline" size="icon-sm" disabled={!hasNext} onClick={onNext}>
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default CursorPagination;
